import type { WcRenderSurface, WcVideoFrame } from '../client/WebCodecsClientTypes';

export interface WcAnimationPlatform {
  requestFrame(callback: () => void): number;
  cancelFrame(id: number): void;
  nowUs(): number;
}
export interface WcRenderEvents {
  presented(frameId: number, intervalUs: number | null): void;
  dropped(frameId: number): void;
  failed(reason: string): void;
}
/** One frame owns one close. Draw submission is a presentation proxy, not physical scanout. */
export class WebCodecsRenderer {
  private pending: { frame: WcVideoFrame; id: number; receivedUs: number; arrivalUs?: number } | null = null;
  private animation: number | null = null;
  private visible = true;
  private closed = false;
  private lastPaintUs: number | null = null;
  private pictureWidth = 0;
  private pictureHeight = 0;
  private maxGapUs = 0;
  private presentedFrames = 0;
  private readonly latencyUs: number[] = [];
  private readonly unlisten: () => void;
  constructor(private readonly surface: WcRenderSurface, private readonly platform: WcAnimationPlatform,
    private readonly events: WcRenderEvents) {
    this.unlisten = surface.onLost(() => this.fail('Render context lost'));
  }
  configure(width: number, height: number): void {
    this.clear();
    if (this.pictureWidth === width && this.pictureHeight === height) return;
    this.surface.resize(width, height);
    this.pictureWidth = width; this.pictureHeight = height;
  }
  accept(frame: WcVideoFrame, frameId: number, arrivalUs?: number): void {
    if (this.closed || !this.visible) { frame.close(); this.events.dropped(frameId); return; }
    this.dropPending(); this.pending = { frame, id: frameId, receivedUs: this.platform.nowUs(), arrivalUs };
    if (this.animation === null) {
      try { this.animation = this.platform.requestFrame(() => this.paint()); }
      catch { this.fail('Worker animation scheduling unavailable'); }
    }
  }
  setVisible(visible: boolean): void { this.visible = visible; if (!visible) { this.clear(); this.lastPaintUs = null; } }
  getPending(): number { return this.pending ? 1 : 0; }
  oldestAgeUs(): number { return this.pending ? Math.max(0, this.platform.nowUs() - this.pending.receivedUs) : 0; }
  presentedCount(): number { return this.presentedFrames; }
  /** Longest completed gap, or the open gap since the last paint while the canvas still shows it. */
  maxPresentGapMs(): number {
    const open = this.lastPaintUs === null ? 0 : Math.max(0, this.platform.nowUs() - this.lastPaintUs);
    return Math.floor(Math.max(this.maxGapUs, open) / 1000);
  }
  drainLatencySamplesUs(): number[] { return this.latencyUs.splice(0, 2048); }
  /** Drops an unpainted frame only. The last presented image stays on the canvas. */
  clear(): void {
    if (this.animation !== null) this.platform.cancelFrame(this.animation);
    this.animation = null; this.dropPending();
  }
  close(): void { if (!this.closed) { this.closed = true; this.clear(); this.unlisten(); } }
  private paint(): void {
    this.animation = null; const pending = this.pending; this.pending = null;
    if (!pending) return;
    let painted = false;
    try { if (this.visible && !this.closed) { this.surface.draw(pending.frame); painted = true; } }
    catch { this.fail('Canvas draw failed'); }
    finally { pending.frame.close(); }
    if (!painted) { this.events.dropped(pending.id); return; }
    const now = this.platform.nowUs(), interval = this.lastPaintUs === null ? null : Math.max(0, now - this.lastPaintUs);
    if (interval !== null) this.maxGapUs = Math.max(this.maxGapUs, interval);
    if (pending.arrivalUs !== undefined) {
      this.latencyUs.push(Math.max(0, now - pending.arrivalUs));
      if (this.latencyUs.length > 2048) this.latencyUs.splice(0, this.latencyUs.length - 2048);
    }
    this.lastPaintUs = now; this.presentedFrames += 1; this.events.presented(pending.id, interval);
  }
  private dropPending(): void {
    const pending = this.pending; this.pending = null;
    if (pending) { pending.frame.close(); this.events.dropped(pending.id); }
  }
  private fail(reason: string): void { if (!this.closed) { this.close(); this.events.failed(reason); } }
}
